import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ReportTheme } from '../../types';
import { getChartColor } from './chartUtils';

interface BrowserPerformanceBarChartWidgetProps {
  data: any;
  data_points?: string[];
  report_start_date?: string;
  report_end_date?: string;
  reportTheme: ReportTheme;
}

export const BrowserPerformanceBarChartWidget: React.FC<BrowserPerformanceBarChartWidgetProps> = ({ 
  data, 
  data_points,
  report_start_date,
  report_end_date,
  reportTheme 
}) => {
  console.log('BrowserPerformanceBarChartWidget raw data:', data);

  // Process the raw API data internally for Browser Performance
  const processData = () => {
    if (!data || !data.data || typeof data.data !== 'object') {
      console.warn('BrowserPerformanceBarChartWidget: Invalid data structure');
      return null;
    }

    const rawData = data.data;
    const dateEntries = Object.entries(rawData);
    
    if (dateEntries.length === 0) {
      console.warn('BrowserPerformanceBarChartWidget: No data entries found');
      return null;
    }

    // Aggregate browser data across all dates
    const browserTotals: { [key: string]: number } = {};

    dateEntries.forEach(([dateKey, dayData]: [string, any]) => {
      if (!dayData || typeof dayData !== 'object') {
        return;
      }

      // Process each browser in the day's data (look for browser_ prefix)
      Object.entries(dayData).forEach(([browserKey, browserData]: [string, any]) => {
        if (browserKey.startsWith('browser_') && browserData && typeof browserData === 'object') {
          const sessions = browserData.sessions || browserData.count || 0;
          const browserName = browserKey.replace('browser_', '');
          browserTotals[browserName] = (browserTotals[browserName] || 0) + sessions;
        }
      });
    });

    if (Object.keys(browserTotals).length === 0) {
      console.warn('BrowserPerformanceBarChartWidget: No browser data found');
      return null;
    }
    
    const chartData = Object.entries(browserTotals)
      .map(([browserKey, sessions], index) => ({
        name: browserKey,
        sessions: sessions,
        color: getChartColor(index, reportTheme.theme)
      }))
      .sort((a, b) => b.sessions - a.sessions) // Sort by sessions descending
      .slice(0, 10); // Take top 10 browsers

    return chartData.length > 0 ? chartData : null;
  };

  const processedData = processData();

  if (!processedData || processedData.length === 0) {
    return null;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={processedData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis />
        <Tooltip />
        <Bar 
          dataKey="sessions" 
          fill={getChartColor(0, reportTheme.theme)} 
          name="Sessions"
        />
      </BarChart>
    </ResponsiveContainer>
  );
};


